import React from 'react';
import styled from 'astroturf/react';
import routes from 'js/core_pre_webpack/routes';
import { Button } from 'js/design_system/Button';
import { Icon } from 'js/design_system/Icon';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';

const Container = styled.a`
  display: block;
  width: 100%;
  background: #ffffff;
  border: 1px solid #dddde2;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  text-decoration: none;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    text-decoration: none;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  align-items: stretch;
  min-height: 164px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ImageSection = styled.div`
  position: relative;
  width: 283px;
  min-height: 164px;
  background: #93c1c4;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;

  @media (max-width: 768px) {
    width: 100%;
    min-height: 140px;
  }
`;

const AudioPlayer = styled.div`
  background: #ffffff;
  border-radius: 8px;
  padding: 15px 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 235px;
  width: 100%;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #000000;
`;

const GrayIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #999999;
  opacity: 0.6;
`;

const TimeText = styled.p`
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: normal;
  color: #000000;
  margin: 0;
  white-space: nowrap;
  flex-shrink: 0;
`;

const Timeline = styled.div`
  flex: 1;
  height: 4px;
  background: #d9d9d9;
  border-radius: 200px;
  overflow: hidden;
`;

const TextSection = styled.div`
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const Title = styled.p`
  font-family: 'DM Sans', sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 32px;
  color: #141413;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 18px;
    line-height: 28px;
  }
`;

const Description = styled.p`
  font-family: 'DM Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  color: #141413;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 18px;
  }
`;

const StyledButton = styled(Button)`
  background: #2c5f5d;
  border-color: #2c5f5d;
  color: #ffffff;
  height: 36px;
  padding: 0 16px;
  font-size: 14px;

  &:hover {
    background: #245049;
    border-color: #245049;
  }

  &:active {
    background: #1d403a;
    border-color: #1d403a;
  }
`;

/**
 * Podcast Library Ad component that promotes the podcast library feature.
 * Displays as a clickable card in the home feed linking to the podcast library.
 *
 * This component is automatically tracked with impression watching.
 */
export const PodcastLibraryAd1: React.FC = () => {
  const impressionSource = 'podcast_lib.home.ad-1';

  return (
    <ImpressionWatcher active={true} source={impressionSource}>
      <Container href={routes.podcast_library_url()}>
        <ContentWrapper>
          <ImageSection>
            <AudioPlayer>
              <IconWrapper>
                <Icon name="play_arrow" size={22} />
              </IconWrapper>
              <TimeText>0:00/ 4:45</TimeText>
              <Timeline />
              <GrayIconWrapper>
                <Icon name="volume_up" size={22} />
              </GrayIconWrapper>
            </AudioPlayer>
          </ImageSection>
          <TextSection>
            <TextContent>
              <Title>Discover 35K+ research podcasts</Title>
              <Description>
                Explore research podcasts in Mathematics, Algebra and Abelian
                groups
              </Description>
            </TextContent>
            <ButtonWrapper>
              <StyledButton size="sm" iconRight="arrow_forward">
                Browse podcast library
              </StyledButton>
            </ButtonWrapper>
          </TextSection>
        </ContentWrapper>
      </Container>
    </ImpressionWatcher>
  );
};
